// src/Context/AuthContext.js
// Decodes JWT on login and exposes: user.roles, user.is_minor, user.alias
// Helper hooks: useIsGuardian, useIsCoach, useIsPlayer, useIsMinor
//
// FIX: unified all token reads to check gt_token first (then legacy "token" key as fallback).
//      Previously init and focus-recheck only read localStorage["token"], while LoginModal
//      writes to localStorage["gt_token"] — mismatch caused spurious logout on tab-focus.
//      All writes now set BOTH keys for backwards compatibility with other components.

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

export const AuthContext = createContext(null);

function decodeJWT(token) {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - b64.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

function buildUser(token) {
  if (!token) return null;
  const payload = decodeJWT(token);
  if (!payload) return null;

  // Check expiry
  const nowSec = Math.floor(Date.now() / 1000);
  if (payload.exp && payload.exp < nowSec) return null;

  // Normalise roles — always an array
  let roles = payload.roles || ["player"];
  if (typeof roles === "string") {
    roles = roles.split(",").map(function(r) { return r.trim(); }).filter(Boolean);
  }
  if (!Array.isArray(roles)) roles = ["player"];

  return {
    id:       payload.user_id || payload.sub,
    user_id:  payload.user_id || payload.sub,
    sub:      payload.sub     || payload.user_id,
    email:    payload.email   || "",
    alias:    payload.alias   || "",
    roles,
    is_minor: payload.is_minor === true || payload.is_minor === 1,
  };
}

// Read the best available token — gt_token preferred, "token" as legacy fallback
function readStoredToken() {
  try {
    return localStorage.getItem("gt_token") || localStorage.getItem("token") || null;
  } catch (e) {
    return null;
  }
}

// Write token to both keys so all parts of the app see it
function writeStoredToken(token) {
  try {
    localStorage.setItem("gt_token", token);
    localStorage.setItem("token", token);
  } catch (e) {}
}

function clearStoredToken() {
  try {
    localStorage.removeItem("gt_token");
    localStorage.removeItem("token");
  } catch (e) {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(function() {
    const token = readStoredToken();
    return token ? buildUser(token) : null;
  });

  // Re-check token expiry on focus
  useEffect(function() {
    function check() {
      const token = readStoredToken();
      if (!token) { setUser(null); return; }
      const u = buildUser(token);
      if (!u) {
        clearStoredToken();
        try { sessionStorage.removeItem("gt_token"); } catch (e) {}
        setUser(null);
      }
    }
    window.addEventListener("focus", check);
    return function() { window.removeEventListener("focus", check); };
  }, []);

  const login = useCallback(function(token, userOverride) {
    if (!token) return;
    writeStoredToken(token);
    // userOverride lets LoginModal pass the full API response user object
    // so roles/is_minor from the API are used directly (more reliable than JWT)
    if (userOverride && userOverride.roles) {
      let roles = userOverride.roles;
      if (typeof roles === "string") {
        roles = roles.split(",").map(function(r) { return r.trim(); }).filter(Boolean);
      }
      setUser({
        id:       userOverride.id       || userOverride.user_id,
        user_id:  userOverride.id       || userOverride.user_id,
        sub:      userOverride.id       || userOverride.user_id,
        email:    userOverride.email    || "",
        alias:    userOverride.alias    || "",
        roles,
        is_minor: userOverride.is_minor === true || userOverride.is_minor === 1,
      });
    } else {
      setUser(buildUser(token));
    }
  }, []);

  const logout = useCallback(function() {
    clearStoredToken();
    try {
      sessionStorage.removeItem("gt_token");
      sessionStorage.removeItem("gt_user");
    } catch (e) {}
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

// ── Role helper hooks ──────────────────────────────────────────────────────────

export function useIsPlayer() {
  const { user } = useContext(AuthContext);
  return !!(user && user.roles && user.roles.includes("player"));
}

export function useIsGuardian() {
  const { user } = useContext(AuthContext);
  return !!(user && user.roles && user.roles.includes("guardian"));
}

export function useIsCoach() {
  const { user } = useContext(AuthContext);
  return !!(user && user.roles && user.roles.includes("coach"));
}

export function useIsMinor() {
  const { user } = useContext(AuthContext);
  return !!(user && user.is_minor);
}

export function useHasRole(role) {
  const { user } = useContext(AuthContext);
  return !!(user && user.roles && user.roles.includes(role));
}